// src/features/user-portfolios/api.ts
//
// Data layer for the User Portfolios domain. Talks to the FastAPI
// user_portfolios router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>), and
//   - already bakes the `/api/v1` version prefix into its baseURL.
// We therefore request the bare `/portfolios/*` path here — adding `/api/v1`
// would double the prefix.

import { apiClient } from '../../api/client';
import type { InstrumentType } from './store/usePortfolioStore';

const BASE_URL = '/portfolios';

// The reporting base currencies a portfolio can be measured in. GBP is
// supported now that LSE assets are stored natively as GBX and converted via
// the FX hub's GBP*0.01 scaling.
export type CurrencyCode = 'USD' | 'EUR' | 'GBP';

// Mirrors the backend PortfolioResponse schema.
export interface Portfolio {
    id: number;
    user_id: number;
    name: string;
    // The reporting base currency the whole portfolio is measured in.
    base_currency: string;
    created_at?: string | null;
}

// Mirrors the backend TransactionCreateRequest body.
export interface TransactionCreatePayload {
    symbol: string;
    instrument_name?: string | null;
    instrument_type?: string | null;
    sector?: string | null;
    transaction_type: 'BUY' | 'SELL';
    add_date?: string; // YYYY-MM-DD; backend defaults to today when omitted
    shares: number;
    price_per_share: number;
    broker_fees?: number;
    // The IMMUTABLE native currency this specific trade occurred in. ANY 3-letter
    // ISO code (USD, EUR, CAD, GBP, JPY, MXN...) -- the backend's Any-to-Any USD
    // hub converts it to the portfolio's base currency at read-time.
    currency?: string;
}

// Mirrors the backend TransactionResponse schema.
export interface TransactionResponse {
    id: number;
    portfolio_id: number;
    symbol: string;
    instrument_name?: string | null;
    instrument_type?: string | null;
    sector?: string | null;
    transaction_type: string;
    add_date: string;
    shares: number;
    price_per_share: number;
    broker_fees: number;
    currency: string;
}

// GET /api/v1/portfolios — the current user's portfolios (newest first).
export const fetchPortfolios = async (): Promise<Portfolio[]> => {
    const { data } = await apiClient.get<Portfolio[]>(BASE_URL);
    return data;
};

// POST /api/v1/portfolios — create a new portfolio (enforces the max-6 cap).
export const createPortfolio = async (
    name: string,
    baseCurrency: CurrencyCode = 'USD',
): Promise<Portfolio> => {
    const { data } = await apiClient.post<Portfolio>(BASE_URL, {
        name,
        base_currency: baseCurrency,
    });
    return data;
};

// PATCH /api/v1/portfolios/{id} — partial update (rename and/or re-base).
//
// The backend PortfolioUpdateRequest now treats BOTH fields as optional, so a
// caller can patch just one facet: the dashboard's "View In" lens sends only
// ``base_currency`` (name untouched) and the Hub inline editor may send only
// ``name`` (currency untouched). Omitted fields are never clobbered.
export const updatePortfolio = async (
    id: number | string,
    payload: { name?: string; base_currency?: CurrencyCode },
): Promise<Portfolio> => {
    const { data } = await apiClient.patch<Portfolio>(`${BASE_URL}/${id}`, payload);
    return data;
};

// DELETE /api/v1/portfolios/{id} — delete a portfolio (cascades transactions).
export const deletePortfolio = async (id: number | string): Promise<void> => {
    await apiClient.delete(`${BASE_URL}/${id}`);
};

// POST /api/v1/portfolios/{id}/transactions — append a Buy/Sell entry.
export const addTransaction = async (
    portfolioId: number | string,
    payload: TransactionCreatePayload,
): Promise<TransactionResponse> => {
    const { data } = await apiClient.post<TransactionResponse>(
        `${BASE_URL}/${portfolioId}/transactions`,
        payload,
    );
    return data;
};

// ---------------------------------------------------------------------------
// Dashboard — triggers the heavy Numba FIFO + Polars engine on the backend.
// ---------------------------------------------------------------------------
// ALL figures are expressed in the portfolio's ``base_currency``.
export interface DashboardMetrics {
    base_currency: string;
    total_market_value: number;
    total_cost_basis: number;
    total_unrealized_currency: number;
    total_unrealized_percent: number;
    total_realized_currency: number;
    simple_return_percent: number;
    all_time_roi: number;           // money-weighted All-Time Net ROI
    inception: number;
    ytd: number;
    one_year: number;
    benchmark_ytd: number;
}

export interface PositionRow {
    symbol: string;
    instrument: string;
    instrument_name?: string | null;
    instrument_type?: string | null;
    sector?: string | null;
    position: number;
    add_date?: string | null;

    // Currency awareness: the asset's own listing currency vs the portfolio's
    // reporting base currency (equal for a same-currency asset).
    native_currency: string;
    base_currency: string;

    // Every monetary metric is split: ``_native`` is the raw figure in the
    // asset's own currency (drives the per-asset view); ``_base`` is the
    // FX-converted figure that feeds the portfolio totals.
    average_price_native: number;
    average_price_base: number;
    broker_fees_native: number;
    broker_fees_base: number;
    last_market_price_native: number;
    last_market_price_base: number;
    cost_basis_native: number;
    cost_basis_base: number;
    daily_pnl_currency_native: number;
    daily_pnl_currency_base: number;
    total_market_value_native: number;
    total_market_value_base: number;
    realized_pnl_native: number;
    realized_pnl_base: number;
    unrealized_pnl_native: number;
    unrealized_pnl_base: number;

    // Percentages are currency-agnostic (native-currency ratios).
    daily_pnl_percent: number;
    unrealized_pnl_percent: number;
}

export interface DashboardPerformers {
    top_overall: PositionRow[];
    worst_overall: PositionRow[];
    best_daily: PositionRow[];
    worst_daily: PositionRow[];
}

// Mirrors the backend ClosedLot schema — one realized (closed) SELL tax-lot.
export interface ClosedLot {
    symbol: string;
    instrument_name?: string | null;
    instrument_type?: string | null;
    sector?: string | null;
    sell_date?: string | null;
    quantity: number;

    // Currencies + split native/base figures (point-in-time FX converted).
    native_currency: string;
    base_currency: string;
    sell_price_native: number;
    sell_price_base: number;
    fees_native: number;
    fees_base: number;
    cost_basis_sold_native: number;
    cost_basis_sold_base: number;
    realized_pnl_native: number;
    realized_pnl_base: number;
    return_pct: number;
}

// Mirrors the backend ChartDataPoint schema — one daily point on the
// performance chart (raw cumulative TWR + benchmark close, rebased client-side).
export interface ChartDataPoint {
    date: string;
    portfolio_twr: number;
    benchmark_value: number;
}

export interface DashboardResponse {
    portfolio_id: number;
    name: string;
    base_currency: string;
    positions: PositionRow[];
    closed_history: ClosedLot[];
    metrics: DashboardMetrics;
    performers: DashboardPerformers;
    chart_data: ChartDataPoint[];
    // Last calendar date the server-generated series covers (YYYY-MM-DD), used
    // to anchor client-side period windows to real data. Null when empty.
    last_data_date?: string | null;
}

// GET /api/v1/portfolios/{id}/dashboard — full analysed dashboard payload.
export const getPortfolioDashboard = async (
    id: number | string,
    benchmark: string = 'SPY',
): Promise<DashboardResponse> => {
    const { data } = await apiClient.get<DashboardResponse>(
        `${BASE_URL}/${id}/dashboard`,
        { params: { benchmark } },
    );
    return data;
};

// DELETE /api/v1/portfolios/{id}/positions/{symbol} — wipe a symbol's whole
// ledger history (the row "Del" action).
export const deletePortfolioPosition = async (
    portfolioId: number | string,
    symbol: string,
): Promise<{ status: string }> => {
    const { data } = await apiClient.delete<{ status: string }>(
        `${BASE_URL}/${portfolioId}/positions/${encodeURIComponent(symbol)}`,
    );
    return data;
};

// PUT /api/v1/portfolios/{id}/positions/{symbol} — overwrite a symbol's history
// with a single fresh BUY baseline (the row "Edit" action).
export const editPortfolioPosition = async (
    portfolioId: number | string,
    symbol: string,
    payload: TransactionCreatePayload,
): Promise<{ status: string }> => {
    const { data } = await apiClient.put<{ status: string }>(
        `${BASE_URL}/${portfolioId}/positions/${encodeURIComponent(symbol)}`,
        payload,
    );
    return data;
};

// GET /api/v1/portfolios/{id}/export — download the positions grid as CSV.
//
// Must go through apiClient (not a raw window.location navigation) so the
// WordPress JWT rides along in the Authorization header — the backend export
// route is auth-gated and a plain browser navigation would 401. We pull the
// payload as a Blob and trigger a client-side download.
export const exportPortfolioCsv = async (
    id: number | string,
    defaultFilename = `portfolio_${id}.csv`,
): Promise<void> => {
    // 1. Await the full response object (not just destructured { data }) to access headers
    const response = await apiClient.get<Blob>(`${BASE_URL}/${id}/export`, {
        responseType: 'blob',
    });

    // 2. Safely parse the Content-Disposition header for the server's filename
    let filename = defaultFilename;
    const disposition = response.headers['content-disposition'];

    if (disposition && disposition.includes('filename=')) {
        // Extracts filename="export.csv" or filename=export.csv
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, ''); // strip any quotes
        }
    }

    // 3. Trigger the download using response.data and the dynamic filename
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

// Re-export for convenience so callers can `import type { InstrumentType }`
// from this module alongside the API types.
export type { InstrumentType };
